import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { User, Mail, Building2, Shield, LogOut } from 'lucide-react';

const roleLabels = {
  super_admin: 'Super Admin',
  admin_ente: 'Amministratore Ente',
  operatore_ente: 'Operatore Ente',
  lettore: 'Lettore / Revisore'
};

export default function Profilo() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="space-y-6" data-testid="profilo-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Il Mio Profilo</h1>
        <p className="page-subtitle">Visualizza le informazioni del tuo account</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Profile Card */}
        <Card className="md:col-span-2">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <Avatar className="h-24 w-24">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#0055BB] text-white text-2xl">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="text-center md:text-left">
                <h2 className="text-2xl font-semibold text-slate-900">{user?.name}</h2>
                <p className="text-slate-500">{user?.email}</p>
                <Badge className="mt-2 bg-[#0055BB]/10 text-[#0055BB]">
                  {roleLabels[user?.role] || user?.role}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-[#0055BB]" />
              Informazioni Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Ruolo</p>
                <p className="font-medium">{roleLabels[user?.role] || user?.role}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">ID Ente</p>
                <p className="font-mono text-sm">{user?.ente_id}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Azioni</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate('/impostazioni')}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Impostazioni Ente
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci dall'account
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Role Description */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Permessi del tuo ruolo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {user?.role === 'admin_ente' && (
              <>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                  <div>
                    <p className="font-medium">Gestione completa volontari</p>
                    <p className="text-sm text-slate-500">Crea, modifica, cessa volontari</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                  <div>
                    <p className="font-medium">Gestione eventi</p>
                    <p className="text-sm text-slate-500">Crea e gestisci eventi</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                  <div>
                    <p className="font-medium">Presenze e check-in</p>
                    <p className="text-sm text-slate-500">Registra presenze volontari</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                  <div>
                    <p className="font-medium">Coperture assicurative</p>
                    <p className="text-sm text-slate-500">Gestisci le coperture</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                  <div>
                    <p className="font-medium">Documenti</p>
                    <p className="text-sm text-slate-500">Carica e scarica documenti</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                  <div>
                    <p className="font-medium">Report e export</p>
                    <p className="text-sm text-slate-500">Genera PDF e report</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
